import React from "react";
import IntlMessages from "./IntlMessages";
import DOMPurify from "dompurify";

const containsHTML = (str: unknown): boolean => {
   if (typeof str !== "string") return false;
   return /<[a-z][\s\S]*>/i.test(str);
};

// External HTML editors (e.g. CodeMirror) store syntax-highlighted markup where each
// HTML tag is a <span> with escaped entities — textContent recovers the raw HTML source.
const decodeExternalEditorHtml = (str: string): string => {
   if (!/\u037C|&lt;/.test(str)) return str;
   return new DOMParser().parseFromString(str, "text/html").body.textContent ?? "";
};

export const SafeCellContent = ({
   content,
   _depth = 0,
}: {
   content: unknown;
   _depth?: number;
}): React.ReactElement => {
   if (_depth > 10) return <>-</>;
   const emptyCell = <>-</>;
   if (content == null) return emptyCell;
   if (React.isValidElement(content)) return content;

   if (typeof content === "number") return <>{content}</>;

   if (typeof content === "string") {
      const decoded = decodeExternalEditorHtml(content);
      if (containsHTML(decoded)) {
         const cleanHTML = DOMPurify.sanitize(decoded);
         return <p dangerouslySetInnerHTML={{ __html: cleanHTML }} />;
      }
      return decoded === "" ? emptyCell : <>{decoded}</>;
   }

   if (Array.isArray(content)) {
      if (content.length === 0) return emptyCell;
      return (
         <div>
            {content.map((item, i) => (
               <p key={item.id ?? i}>
                  <SafeCellContent content={item} _depth={_depth + 1} />
               </p>
            ))}
         </div>
      );
   }

   if (content && typeof content === "object") {
      const obj = content as Record<string, unknown>;
      // File handle
      if (obj.fileUrl && obj.name) {
         const isValidUrl =
            typeof obj.fileUrl === "string" &&
            (obj.fileUrl.startsWith("http://") || obj.fileUrl.startsWith("https://"));
         if (!isValidUrl) {
            return <>{obj.name}</>;
         }
         return (
            <a
               href={obj.fileUrl as string}
               target="_blank"
               rel="noopener noreferrer"
               aria-label={`Otwórz plik: ${obj.name}`}
            >
               {obj.name as string}
            </a>
         );
      }
      if (obj.label != null || obj.value != null) {
         return <SafeCellContent content={obj.label ?? obj.value ?? "-"} _depth={_depth + 1} />;
      }
      // Fallback for other objects
      try {
         return <pre style={{ whiteSpace: "pre-wrap" }}>{JSON.stringify(content)}</pre>;
      } catch (error) {
         return (
            <p style={{ color: "red" }}>
               <IntlMessages id="error.objectRenderingError" />
            </p>
         );
      }
   }
   return emptyCell;
};
